import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Wrench, MessageSquare, CheckCircle2, XCircle } from "lucide-react";

export default function AgentTracePanel({ trace }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    const { toolCallCount, iterations } = useMemo(() => {
        const calls = trace.filter((e) => e.type === "tool_call").length;
        const groups = new Map();
        for (const entry of trace) {
            const key = entry.iteration ?? "final";
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(entry);
        }
        return { toolCallCount: calls, iterations: [...groups.entries()] };
    }, [trace]);

    if (!trace?.length) return null;

    return (
        <div className="border rounded-md bg-muted/30 text-sm">
            <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-2 w-full px-3 py-2 text-left text-muted-foreground hover:text-foreground transition-colors"
            >
                {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                <Wrench className="w-3.5 h-3.5" />
                <span>{t("admin.support.agentActivity", "AI Agent Activity")} ({toolCallCount} {t("admin.support.toolCalls", "tool calls")})</span>
            </button>
            {expanded && (
                <div className="px-3 pb-3 space-y-2">
                    {iterations.map(([iteration, entries]) => (
                        <IterationGroup key={iteration} iteration={iteration} entries={entries} />
                    ))}
                </div>
            )}
        </div>
    );
}

function IterationGroup({ iteration, entries }) {
    return (
        <div className="space-y-1">
            {iteration !== "final" && (
                <div className="text-[11px] text-muted-foreground/60 font-medium uppercase tracking-wide pt-1">
                    Iteration {iteration}
                </div>
            )}
            {entries.map((entry, i) => (
                <TraceEntry key={i} entry={entry} />
            ))}
        </div>
    );
}

function TraceEntry({ entry }) {
    const [open, setOpen] = useState(false);

    if (entry.type === "tool_call") {
        return (
            <div className="rounded bg-background border px-2.5 py-1.5">
                <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 w-full text-left text-xs">
                    <Wrench className="w-3 h-3 text-blue-500 shrink-0" />
                    <span className="font-medium">{entry.toolName}</span>
                    {open ? <ChevronDown className="w-3 h-3 ml-auto" /> : <ChevronRight className="w-3 h-3 ml-auto" />}
                </button>
                {open && (
                    <pre className="mt-1 text-[11px] text-muted-foreground bg-muted rounded p-1.5 overflow-x-auto whitespace-pre-wrap break-all">
                        {JSON.stringify(entry.args, null, 2)}
                    </pre>
                )}
            </div>
        );
    }

    if (entry.type === "tool_result") {
        const Icon = entry.success ? CheckCircle2 : XCircle;
        const color = entry.success ? "text-green-500" : "text-red-500";
        return (
            <div className="flex items-start gap-1.5 px-2.5 py-1 text-xs text-muted-foreground">
                <Icon className={`w-3 h-3 mt-0.5 shrink-0 ${color}`} />
                <span className="break-all">{entry.summary}</span>
            </div>
        );
    }

    if (entry.type === "assistant") {
        return (
            <div className="flex items-start gap-1.5 px-2.5 py-1.5 text-xs text-muted-foreground">
                <MessageSquare className="w-3 h-3 mt-0.5 shrink-0 text-purple-500" />
                <span className="break-all line-clamp-3">{entry.content.replace(/<[^>]*>/g, "").slice(0, 200)}</span>
            </div>
        );
    }

    return null;
}
